import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import {
  Search,
  Download,
  Loader2,
  Calendar,
  AlertTriangle,
  Eye,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import AdminReportDetail from "@/components/AdminReportDetail";

export default function AdminReports() {
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [caregiverId, setCaregiverId] = useState("");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (start) params.start = start;
      if (end) params.end = end;
      if (clientId) params.client_id = clientId;
      if (caregiverId) params.caregiver_id = caregiverId;
      const { data } = await api.get("/reports", { params });
      setReports(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/auth/caregivers")])
      .then(([c, cg]) => {
        setClients(c.data);
        setCaregivers(cg.data);
      });
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (id) => {
    const res = await fetch(`${API_BASE}/reports/${id}/pdf`, {
      credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkDownload = async () => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (caregiverId) params.set("caregiver_id", caregiverId);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    const res = await fetch(`${API_BASE}/admin/reports/bulk-pdf?${params}`, {
      credentials: "include",
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      toast.error(data.detail || "Bulk export failed");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-reports.pdf";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Bulk PDF downloaded");
  };

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Records</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            All reports
          </h1>
        </div>
        <button onClick={bulkDownload} className="btn-primary" data-testid="bulk-export-button">
          <FileDown className="h-4 w-4" /> Bulk PDF export
        </button>
      </div>

      <div className="card-surface p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-clinical-subtext" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search notes, clients, caregivers…"
              className="input-clinical pl-9"
              data-testid="search-input"
            />
          </div>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="input-clinical md:col-span-3"
            data-testid="filter-client"
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={caregiverId}
            onChange={(e) => setCaregiverId(e.target.value)}
            className="input-clinical md:col-span-3"
            data-testid="filter-caregiver"
          >
            <option value="">All caregivers</option>
            {caregivers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="input-clinical md:col-span-1"
            data-testid="filter-start"
          />
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="input-clinical md:col-span-1"
            data-testid="filter-end"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={fetchData} className="btn-secondary" data-testid="apply-filters">
            Apply filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : reports.length === 0 ? (
        <div className="card-surface p-10 text-center text-clinical-subtext">
          No reports match your filters.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="reports-list">
          {reports.map((r) => (
            <li
              key={r.id}
              className="card-surface p-4 flex items-center gap-4 transition-colors hover:border-clinical-primary/30"
              data-testid={`report-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-base font-medium truncate">
                    {r.client_name}
                  </h3>
                  {r.incident_flag && (
                    <span className="pill bg-destructive/10 text-destructive">
                      <AlertTriangle className="h-3 w-3" /> Incident
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-clinical-subtext">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {r.shift_date} · <span className="capitalize">{r.shift_type}</span>
                  </span>
                  <span>by {r.caregiver_name}</span>
                </div>
                {(r.formatted_notes || r.raw_notes) && (
                  <p className="mt-2 text-xs text-clinical-subtext line-clamp-2">
                    {r.formatted_notes || r.raw_notes}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => download(r.id)}
                  className="btn-ghost"
                  data-testid={`download-${r.id}`}
                  title="Quick PDF download"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDetailId(r.id)}
                  className="btn-secondary"
                  data-testid={`review-${r.id}`}
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden sm:inline">Review &amp; share</span>
                  <span className="sm:hidden">Review</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {detailId && (
        <AdminReportDetail reportId={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}
